/**
 * ContractSigning.jsx — Contract signing flow for buyers and bidders.
 *
 * BUYER flow:
 *   1. Choose: "Reveal Bidder Identity" OR "Proceed to Contract"
 *   2. Download contract template → fill in details → upload signed PDF
 *   3. Wait for admin review
 *
 * BIDDER flow:
 *   1. Notified by email when buyer's contract is approved
 *   2. Download contract template → sign → upload signed PDF
 *   3. Wait for admin review → buyer identity revealed on approval
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Chip,
  Divider,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { apiClient } from '../../utils/auth';

// Contract template download URL — the PDF from the Documents folder served as static
const CONTRACT_TEMPLATE_URL = '/contract_template.pdf';

const STATUS_LABELS = {
  PENDING_BUYER_UPLOAD:  'Waiting for buyer to upload signed contract',
  BUYER_UPLOADED:        'Buyer contract under admin review',
  ADMIN_SENT_TO_BIDDER:  'Admin approved — waiting for bidder',
  PENDING_BIDDER_UPLOAD: 'Waiting for bidder to upload signed contract',
  BIDDER_UPLOADED:       'Bidder contract under admin review',
  COMPLETED:             'Contract fully signed — deal active',
  REJECTED_BUYER:        'Buyer contract rejected — re-upload required',
  REJECTED_BIDDER:       'Bidder contract rejected — re-upload required',
};

const BUYER_STEPS = ['Initiate Contract', 'Upload Signed Contract', 'Admin Review', 'Bidder Signs', 'Complete'];
const BIDDER_STEPS = ['Await Buyer', 'Upload Signed Contract', 'Admin Review', 'Complete'];

function buyerStepIndex(status) {
  if (!status || status === 'PENDING_BUYER_UPLOAD') return 0;
  if (status === 'BUYER_UPLOADED') return 1;
  if (status === 'REJECTED_BUYER') return 1;
  if (status === 'PENDING_BIDDER_UPLOAD' || status === 'ADMIN_SENT_TO_BIDDER') return 2;
  if (status === 'BIDDER_UPLOADED') return 3;
  if (status === 'COMPLETED') return 4;
  return 0;
}

function bidderStepIndex(status) {
  if (!status || status === 'PENDING_BUYER_UPLOAD' || status === 'BUYER_UPLOADED' || status === 'REJECTED_BUYER') return 0;
  if (status === 'PENDING_BIDDER_UPLOAD' || status === 'ADMIN_SENT_TO_BIDDER') return 1;
  if (status === 'BIDDER_UPLOADED') return 2;
  if (status === 'REJECTED_BIDDER') return 2;
  if (status === 'COMPLETED') return 3;
  return 0;
}

export default function ContractSigning({
  dealId,
  isBuyer,
  onIdentityRevealRequest,
  revealRequested = false,
  anonymityReleaseFlag = false,
  revealLoading = false,
}) {
  const [contract, setContract]         = useState(null);
  const [loading, setLoading]           = useState(true);
  const [uploading, setUploading]       = useState(false);
  const [error, setError]               = useState('');
  const [file, setFile]                 = useState(null);
  const [showChoice, setShowChoice]     = useState(false); // buyer initial choice

  const fetchContract = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/api/v1/deals/${dealId}/contract/`);
      setContract(data.contract);
      // Show choice panel if buyer and no contract yet
      if (isBuyer && !data.contract) setShowChoice(true);
    } catch {
      setContract(null);
      if (isBuyer) setShowChoice(true);
    } finally {
      setLoading(false);
    }
  }, [dealId, isBuyer]);

  useEffect(() => { fetchContract(); }, [fetchContract]);

  const handleInitiateContract = async () => {
    setError('');
    setUploading(true);
    try {
      const { data } = await apiClient.post(`/api/v1/deals/${dealId}/contract/initiate/`);
      setContract(data.contract);
      setShowChoice(false);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to initiate contract.');
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async () => {
    if (!file) { setError('Please select a PDF file.'); return; }
    setError('');
    setUploading(true);
    const formData = new FormData();
    formData.append('contract_file', file);
    const endpoint = isBuyer
      ? `/api/v1/deals/${dealId}/contract/buyer-upload/`
      : `/api/v1/deals/${dealId}/contract/bidder-upload/`;
    try {
      const { data } = await apiClient.post(endpoint, formData);
      setContract(data.contract);
      setFile(null);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <CircularProgress size={24} />;

  const status = contract?.status || null;
  const steps  = isBuyer ? BUYER_STEPS : BIDDER_STEPS;
  const activeStep = isBuyer ? buyerStepIndex(status) : bidderStepIndex(status);

  // ── Buyer initial choice ──
  if (isBuyer && showChoice && !contract) {
    return (
      <Box>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          You've selected a winner. How would you like to proceed?
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
          <Paper variant="outlined" sx={{ p: 3, flex: 1, minWidth: 220 }}>
            <Typography variant="h6" gutterBottom>🔍 Reveal Bidder Identity First</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Request admin to reveal the bidder's basic details (name, email) before signing the contract.
              Admin approval required — you will be notified once approved.
            </Typography>
            <Button
              variant="outlined"
              onClick={onIdentityRevealRequest}
              disabled={uploading || revealRequested || anonymityReleaseFlag || revealLoading}
              fullWidth
            >
              {revealLoading || uploading ? (
                <CircularProgress size={18} />
              ) : anonymityReleaseFlag ? (
                'Approved by Admin'
              ) : revealRequested ? (
                'Requested'
              ) : (
                'Request Identity Reveal'
              )}
            </Button>
          </Paper>

          <Paper variant="outlined" sx={{ p: 3, flex: 1, minWidth: 220 }}>
            <Typography variant="h6" gutterBottom>📝 Proceed Directly to Contract</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Skip identity reveal and go straight to signing the Cybersecurity Services Agreement.
              Bidder identity stays anonymous until both parties sign.
            </Typography>
            <Button
              variant="contained"
              disabled={uploading}
              onClick={handleInitiateContract}
              fullWidth
            >
              {uploading ? <CircularProgress size={20} /> : 'Start Contract Process'}
            </Button>
          </Paper>
        </Box>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Box>
    );
  }

  if (!contract) return null;

  const canBuyerUpload  = isBuyer  && (status === 'PENDING_BUYER_UPLOAD'  || status === 'REJECTED_BUYER');
  const canBidderUpload = !isBuyer && (status === 'PENDING_BIDDER_UPLOAD' || status === 'REJECTED_BIDDER');
  const canUpload = canBuyerUpload || canBidderUpload;

  return (
    <Box>
      {/* Progress stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 3 }} alternativeLabel>
        {steps.map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {/* Status chip */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Chip
          label={STATUS_LABELS[status] || status}
          color={status === 'COMPLETED' ? 'success' : status?.includes('REJECTED') ? 'error' : 'info'}
          icon={status === 'COMPLETED' ? <CheckCircleIcon /> : <HourglassEmptyIcon />}
        />
      </Box>

      {/* Admin feedback on rejection */}
      {contract.admin_feedback && status?.includes('REJECTED') && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>Admin feedback:</strong> {contract.admin_feedback}
        </Alert>
      )}

      {status === 'COMPLETED' && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Both parties have signed the contract. The engagement is officially active.
          {!isBuyer && ' The buyer\'s identity is now visible in the deal details above.'}
        </Alert>
      )}

      {/* Download template */}
      {canUpload && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Step 1 — Download the contract template</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Download the Cybersecurity Services Agreement, print it, fill in all required fields
            (project name, authorized assets, scope, dates, payment terms, jurisdiction), sign it,
            then scan/photograph it as a PDF and upload below.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            href={CONTRACT_TEMPLATE_URL}
            download="CyberExchange_Contract_Template.pdf"
            target="_blank"
            aria-label="Download contract template"
          >
            Download Contract Template (PDF)
          </Button>
        </Paper>
      )}

      {/* Upload signed contract */}
      {canUpload && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Step 2 — Upload your signed contract</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Upload the signed contract as a PDF (max 10 MB). Admin will review it before proceeding.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFileIcon />}
              aria-label="Choose signed contract PDF"
            >
              {file ? file.name : 'Choose PDF'}
              <input
                type="file"
                hidden
                accept=".pdf"
                onChange={(e) => { setFile(e.target.files[0] || null); setError(''); }}
              />
            </Button>
            <Button
              variant="contained"
              disabled={!file || uploading}
              onClick={handleUpload}
              aria-label="Upload signed contract"
            >
              {uploading ? <CircularProgress size={20} color="inherit" /> : 'Upload Signed Contract'}
            </Button>
          </Box>
          {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
        </Paper>
      )}

      {/* Waiting states */}
      {status === 'BUYER_UPLOADED' && isBuyer && (
        <Alert severity="info">
          Your signed contract has been submitted and is under admin review. You will be notified once it is processed.
        </Alert>
      )}
      {status === 'PENDING_BIDDER_UPLOAD' && isBuyer && (
        <Alert severity="info">
          Your contract was approved. The bidder has been notified and is now required to sign and upload their copy.
        </Alert>
      )}
      {(status === 'PENDING_BUYER_UPLOAD' || status === 'BUYER_UPLOADED' || status === 'REJECTED_BUYER') && !isBuyer && (
        <Alert severity="info">
          Waiting for the buyer to complete their contract upload and admin review before your action is required.
        </Alert>
      )}
      {status === 'BIDDER_UPLOADED' && !isBuyer && (
        <Alert severity="info">
          Your signed contract has been submitted and is under admin review. You will be notified once it is processed.
        </Alert>
      )}
    </Box>
  );
}
